import { useEffect, useRef } from 'react';

// usePoll(fn, intervalMs)
// ─────────────────────────────────────────────────────────────────────────────
// Run an async function once on mount, then on a fixed interval thereafter.
// Two safeguards so we don't bankrupt AWS:
//   1. Skip ticks while the tab is hidden (`document.visibilityState`).
//   2. Skip a tick if the previous run hasn't finished (no overlapping calls).
// `fn` is held in a ref so callers can pass an inline arrow without
// re-subscribing the interval on every render.
export default function usePoll(fn, intervalMs = 2500) {
  const ref = useRef(fn);
  useEffect(() => { ref.current = fn; }, [fn]);

  useEffect(() => {
    let inFlight = false;
    let alive = true;
    const tabHidden = () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const tick = async () => {
      if (!alive || inFlight || tabHidden()) return;
      inFlight = true;
      try { await ref.current?.(); }
      catch (_) { /* swallow — polling is best-effort */ }
      finally { inFlight = false; }
    };
    tick();                           // fire once immediately
    const id = setInterval(tick, intervalMs);
    return () => { alive = false; clearInterval(id); };
  }, [intervalMs]);
}
